import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './Counties.module.css';
import SEO from '../components/SEO';

const Counties = () => {
  const [activeCounty, setActiveCounty] = useState(null);
  const svgRef = useRef(null);

  useEffect(() => {
    // Load SVG and manipulate it
    fetch('/usa-ca.svg')
      .then(response => response.text())
      .then(svgText => {
        if (svgRef.current) {
          svgRef.current.innerHTML = svgText;

          // Get the SVG element
          const svgElement = svgRef.current.querySelector('svg');
          if (svgElement) {
            svgElement.setAttribute('class', styles.californiaMap);
            // Remove fixed width/height and let it scale
            svgElement.removeAttribute('width');
            svgElement.removeAttribute('height');
            svgElement.setAttribute('viewBox', '0 0 85.42 143.5');
            svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');

            // Style all counties
            const allPaths = svgElement.querySelectorAll('path');
            allPaths.forEach(path => {
              path.style.fill = '#e2e8f0';
              path.style.stroke = '#94a3b8';
              path.style.strokeWidth = '0.5';
              path.style.transition = 'all 0.3s ease';
              path.style.cursor = 'pointer';
            });

            // Highlight and add interactions to our served counties
            const servedCounties = [
              { id: 'Los Angeles', color: counties[0].color, name: counties[0].fullName },
              { id: 'Orange', color: counties[1].color, name: counties[1].fullName },
              { id: 'Riverside', color: counties[2].color, name: counties[2].fullName },
              { id: 'San Bernardino', color: counties[3].color, name: counties[3].fullName },
              { id: 'Ventura', color: counties[4].color, name: counties[4].fullName }
            ];

            servedCounties.forEach((county, index) => {
              const path = svgElement.querySelector(`#${CSS.escape(county.id)}`);
              if (path) {
                path.style.fill = county.color;
                path.style.fillOpacity = '0.75';
                path.style.stroke = county.color;
                path.style.strokeWidth = '0.5';
                path.style.position = 'relative';

                // Create tooltip element
                const tooltip = document.createElement('title');
                tooltip.textContent = county.name;
                path.appendChild(tooltip);

                path.addEventListener('mouseenter', () => {
                  path.style.fillOpacity = '0.9';
                  path.style.filter = 'brightness(1.1)';
                });

                path.addEventListener('mouseleave', () => {
                  path.style.fillOpacity = '0.75';
                  path.style.filter = '';
                });
              }
            });
          }
        }
      })
      .catch(err => console.error('Error loading SVG:', err));
  }, []);

  const counties = [
    {
      id: 1,
      name: 'Los Angeles',
      fullName: 'Los Angeles County',
      color: '#0073FF',
      description: 'We serve patients throughout Los Angeles County, including the greater Los Angeles area, San Fernando Valley, San Gabriel Valley, and surrounding communities. Our team is familiar with local resources and healthcare networks throughout the region.',
      areas: ['San Fernando Valley', 'San Gabriel Valley', 'Downtown LA', 'Pasadena'],
      mapPath: 'M50,20 L80,25 L90,40 L85,60 L70,75 L50,80 L30,70 L20,50 L25,30 Z',
      gradient: 'linear-gradient(135deg, #0073FF 0%, #0060D9 100%)'
    },
    {
      id: 2,
      name: 'Orange',
      fullName: 'Orange County',
      color: '#FF6B35',
      description: 'Our services extend throughout Orange County, covering North, Central, and South County communities. We work closely with local hospitals, physicians, and care facilities to provide seamless transitions and ongoing support.',
      areas: ['Anaheim', 'Irvine', 'Santa Ana', 'Newport Beach'],
      mapPath: 'M50,30 L75,35 L85,50 L80,70 L60,80 L40,75 L30,60 L35,40 Z',
      gradient: 'linear-gradient(135deg, #FF6B35 0%, #F5602D 100%)'
    },
    {
      id: 3,
      name: 'Riverside',
      fullName: 'Riverside County',
      color: '#4ECDC4',
      description: 'We provide care across Riverside County, from western communities near Orange and Los Angeles counties to the Coachella Valley. Our staff is experienced in serving diverse communities and coordinating care across distances.',
      areas: ['Riverside', 'Corona', 'Murrieta', 'Palm Desert'],
      mapPath: 'M45,25 L70,20 L90,35 L95,55 L85,75 L60,85 L40,80 L28,60 L32,40 Z',
      gradient: 'linear-gradient(135deg, #4ECDC4 0%, #44B3AC 100%)'
    },
    {
      id: 4,
      name: 'San Bernardino',
      fullName: 'San Bernardino County',
      color: '#9B59B6',
      description: 'We serve patients throughout San Bernardino County, including western valleys, mountain communities, and high desert areas. Our team delivers consistent, professional care whether you\'re in suburban or rural settings.',
      areas: ['San Bernardino', 'Rancho Cucamonga', 'Fontana', 'Victorville'],
      mapPath: 'M40,15 L65,18 L85,30 L92,50 L88,70 L70,82 L50,85 L32,75 L25,55 L30,35 Z',
      gradient: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)'
    },
    {
      id: 5,
      name: 'Ventura',
      fullName: 'Ventura County',
      color: '#F39C12',
      description: 'Our coverage includes Ventura County, from coastal communities to inland areas. We understand the unique needs of families throughout the county and coordinate with regional healthcare providers.',
      areas: ['Ventura', 'Oxnard', 'Thousand Oaks', 'Simi Valley'],
      mapPath: 'M48,28 L72,32 L82,45 L78,65 L62,78 L42,74 L32,58 L36,42 Z',
      gradient: 'linear-gradient(135deg, #F39C12 0%, #E67E22 100%)'
    }
  ];

  return (
    <div className={styles.countiesPage}>
      <SEO
        title="Counties We Serve"
        description="All For You Home Health Care serves Los Angeles, Orange, Riverside, San Bernardino, and Ventura counties with professional home health services. Licensed and ACHC accredited."
        keywords="Los Angeles County, Orange County, Riverside County, San Bernardino County, Ventura County, Southern California home health care, service area"
        canonicalUrl="https://allforyouhc.com/counties"
      />

      {/* Hero Section with Interactive Map */}
      <section className={styles.heroSection}>
        <div className={styles.heroContainer}>
          <div className={styles.heroLeft}>
            <h1 className={styles.heroTitle}>
              Counties We Serve
            </h1>
            <p className={styles.heroDescription}>
              All For You Home Health Care provides comprehensive medical and supportive services
              throughout Southern California. Our licensed professionals deliver quality care
              directly to your home.
            </p>

            <div className={styles.countyList}>
              <h3 className={styles.listTitle}>Service Areas</h3>
              <div className={styles.listItems}>
                {counties.map((county) => (
                  <div key={county.id} className={styles.listItem}>
                    <span
                      className={styles.listDot}
                      style={{ backgroundColor: county.color }}
                    ></span>
                    <span className={styles.listText}>{county.fullName}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.heroRight}>
            <div className={styles.californiaMapContainer} ref={svgRef}>
              {/* SVG will be loaded here */}
            </div>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>Not Sure If We Serve Your Area?</h2>
            <p className={styles.ctaText}>
              Contact our office to confirm coverage for your specific location. We're available Monday through Friday,
              10:00 AM to 4:00 PM, and can answer questions about service availability, insurance, and getting started.
            </p>
            <div className={styles.ctaButtons}>
              <a href="tel:8189491031" className={styles.ctaButtonPrimary}>
                Call (818) 949-1031
              </a>
              <Link to="/contact" className={styles.ctaButtonSecondary}>
                Send Us a Message
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Counties;
